import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import {
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Loader2,
  CheckCircle2,
  ArrowUpRight,
  Music2,
  Radio,
  ShieldCheck,
  DollarSign,
  Database,
  Award,
  FileText,
  Handshake,
  Building2,
  Globe2,
  Landmark,
  TrendingUp,
  Cpu,
  Target,
  Flame,
  Fingerprint,
} from "lucide-react";

const STORAGE_KEY = "equantum.audit.draft.v1";

// Field types: text | tel | email | url | number | textarea | radio | checkbox
const sections = [
  {
    id: "artist",
    tag: "SECTION 01",
    title: "The Artist",
    subtitle: "Let's start with you. This information stays private and never gets shared.",
    icon: Fingerprint,
    fields: [
      { name: "artist_name", label: "Artist Name (legal)", type: "text", required: true, placeholder: "e.g. Jordan Rivera" },
      { name: "stage_name", label: "Stage Name", type: "text", placeholder: "Optional" },
      { name: "phone", label: "Phone", type: "tel", required: true, placeholder: "(555) 555-5555" },
      { name: "email", label: "Email", type: "email", required: true, placeholder: "you@example.com" },
      { name: "instagram", label: "Instagram Handle", type: "text", placeholder: "@handle" },
      { name: "tiktok", label: "TikTok Handle", type: "text", placeholder: "@handle" },
      { name: "youtube", label: "YouTube Channel", type: "text", placeholder: "URL or handle" },
      { name: "website", label: "Website", type: "url", placeholder: "https://" },
    ],
  },
  {
    id: "music",
    tag: "SECTION 02",
    title: "The Music",
    subtitle: "Your catalog and your role in it.",
    icon: Music2,
    fields: [
      { name: "songs_released", label: "How many songs have you officially released?", type: "number", min: 0 },
      {
        name: "unreleased_music",
        label: "Do you have unreleased music?",
        type: "radio",
        options: [
          { v: "yes", l: "Yes" },
          { v: "no", l: "No" },
        ],
      },
      {
        name: "roles",
        label: "You are the: (check all that apply)",
        type: "checkbox",
        options: [
          { v: "Artist", l: "Artist" },
          { v: "Songwriter", l: "Songwriter" },
          { v: "Producer", l: "Producer" },
          { v: "Beatmaker", l: "Beatmaker" },
          { v: "Engineer", l: "Engineer" },
        ],
      },
    ],
  },
  {
    id: "distribution",
    tag: "SECTION 03",
    title: "Distribution",
    subtitle: "Who's putting your music on streaming platforms?",
    icon: Radio,
    fields: [
      {
        name: "distributor",
        label: "Who distributes your music?",
        type: "radio",
        options: [
          { v: "DistroKid", l: "DistroKid" },
          { v: "TuneCore", l: "TuneCore" },
          { v: "CD Baby", l: "CD Baby" },
          { v: "UnitedMasters", l: "UnitedMasters" },
          { v: "Too Lost", l: "Too Lost" },
          { v: "AWAL", l: "AWAL" },
          { v: "Other", l: "Other" },
          { v: "", l: "I don't distribute yet" },
        ],
      },
    ],
  },
  {
    id: "publishing",
    tag: "SECTION 04",
    title: "Publishing",
    subtitle: "This is where most artists lose the most money. Be honest.",
    icon: ShieldCheck,
    fields: [
      {
        name: "pro",
        label: "Are you registered with:",
        type: "radio",
        options: [
          { v: "BMI", l: "BMI" },
          { v: "ASCAP", l: "ASCAP" },
          { v: "SESAC", l: "SESAC" },
          { v: "GMR", l: "GMR" },
          { v: "None", l: "None" },
        ],
      },
      {
        name: "publisher_account",
        label: "Do you have a Publisher account, or only a Writer account?",
        type: "radio",
        help: "This one question alone often reveals a major gap. A Writer account collects only ~50% of the publishing money.",
        options: [
          { v: "publisher", l: "Publisher + Writer" },
          { v: "writer_only", l: "Writer only" },
          { v: "dont_know", l: "I don't know" },
        ],
      },
    ],
  },
  {
    id: "royalties",
    tag: "SECTION 05",
    title: "Royalties",
    subtitle: "Which of these are you currently collecting?",
    icon: DollarSign,
    fields: [
      {
        name: "royalties_collected",
        label: "Check all that apply.",
        type: "checkbox",
        options: [
          { v: "performance", l: "Performance Royalties" },
          { v: "mechanical", l: "Mechanical Royalties" },
          { v: "digital_performance", l: "Digital Performance Royalties" },
          { v: "international", l: "International Royalties" },
          { v: "youtube_content_id", l: "YouTube Content ID Revenue" },
          { v: "neighboring", l: "Neighboring Rights" },
          { v: "sync", l: "Sync Income" },
          { v: "publishing", l: "Publishing Revenue" },
          { v: "dont_know", l: "I don't know" },
        ],
      },
    ],
  },
  {
    id: "metadata",
    tag: "SECTION 06",
    title: "Metadata",
    subtitle: "The invisible layer that decides who gets paid.",
    icon: Database,
    fields: [
      {
        name: "metadata_complete",
        label: "Do all your songs have:",
        type: "checkbox",
        options: [
          { v: "isrc", l: "ISRC Codes" },
          { v: "upc", l: "UPC Codes" },
          { v: "songwriter_credits", l: "Songwriter Credits" },
          { v: "producer_credits", l: "Producer Credits" },
          { v: "split_sheets", l: "Split Sheets" },
          { v: "metadata_verified", l: "Metadata Verified" },
          { v: "dont_know", l: "I don't know" },
        ],
      },
    ],
  },
  {
    id: "soundexchange",
    tag: "SECTION 07",
    title: "SoundExchange",
    subtitle: "Digital performance royalties from streaming radio (Pandora, SiriusXM, etc).",
    icon: Award,
    fields: [
      {
        name: "soundexchange",
        label: "Are you registered with SoundExchange?",
        type: "radio",
        options: [
          { v: "yes", l: "Yes" },
          { v: "no", l: "No" },
          { v: "dont_know", l: "I don't know" },
        ],
      },
    ],
  },
  {
    id: "song_registration",
    tag: "SECTION 08",
    title: "Song Registrations",
    subtitle: "An unregistered song generates zero performance royalties.",
    icon: FileText,
    fields: [
      {
        name: "songs_registered_pro",
        label: "Have your songs been registered with your Performing Rights Organization?",
        type: "radio",
        options: [
          { v: "yes", l: "Yes" },
          { v: "no", l: "No" },
        ],
      },
    ],
  },
  {
    id: "splits",
    tag: "SECTION 09",
    title: "Split Agreements",
    subtitle: "This is where friendships end and lawsuits begin.",
    icon: Handshake,
    fields: [
      {
        name: "split_agreements",
        label: "Do you have signed split sheets for every collaboration?",
        type: "radio",
        options: [
          { v: "yes", l: "Yes" },
          { v: "no", l: "No" },
        ],
      },
    ],
  },
  {
    id: "pub_admin",
    tag: "SECTION 10",
    title: "Publishing Administration",
    subtitle: "Who's chasing your global mechanicals + neighboring rights?",
    icon: Building2,
    fields: [
      {
        name: "publishing_admin",
        label: "Who administers your publishing?",
        type: "radio",
        options: [
          { v: "none", l: "None" },
          { v: "songtrust", l: "Songtrust" },
          { v: "sentric", l: "Sentric" },
          { v: "publishing_deal", l: "Publishing Deal" },
          { v: "independent", l: "Independent (self-admin)" },
        ],
      },
    ],
  },
  {
    id: "platforms",
    tag: "SECTION 11",
    title: "Monetization",
    subtitle: "Where is your music currently available?",
    icon: Globe2,
    fields: [
      {
        name: "platforms",
        label: "Check all that apply.",
        type: "checkbox",
        options: [
          { v: "spotify", l: "Spotify" },
          { v: "apple", l: "Apple Music" },
          { v: "amazon", l: "Amazon Music" },
          { v: "youtube_music", l: "YouTube Music" },
          { v: "tiktok", l: "TikTok" },
          { v: "instagram", l: "Instagram" },
          { v: "facebook", l: "Facebook" },
          { v: "pandora", l: "Pandora" },
          { v: "deezer", l: "Deezer" },
          { v: "tidal", l: "Tidal" },
          { v: "audiomack", l: "Audiomack" },
          { v: "soundcloud", l: "SoundCloud" },
        ],
      },
    ],
  },
  {
    id: "business",
    tag: "SECTION 12",
    title: "Business",
    subtitle: "Your music income needs to flow into a business, not you.",
    icon: Landmark,
    fields: [
      {
        name: "business_items",
        label: "Do you currently have:",
        type: "checkbox",
        options: [
          { v: "llc", l: "LLC" },
          { v: "ein", l: "EIN" },
          { v: "business_bank", l: "Business Bank Account" },
          { v: "trademark", l: "Trademark" },
          { v: "business_credit", l: "Business Credit" },
        ],
      },
    ],
  },
  {
    id: "marketing",
    tag: "SECTION 13",
    title: "Marketing",
    subtitle: "Your current audience — no filters.",
    icon: TrendingUp,
    fields: [
      { name: "monthly_listeners", label: "Current Monthly Listeners (Spotify)", type: "number", min: 0 },
      { name: "instagram_followers", label: "Instagram Followers", type: "number", min: 0 },
      { name: "tiktok_followers", label: "TikTok Followers", type: "number", min: 0 },
      { name: "email_list_size", label: "Email List Size", type: "number", min: 0 },
      { name: "biggest_fan_source", label: "Biggest Source of Fans", type: "text", placeholder: "e.g. TikTok, live shows, Spotify editorial…" },
    ],
  },
  {
    id: "ai_ready",
    tag: "SECTION 14",
    title: "Content Readiness",
    subtitle: "What would move the needle for you in the next 90 days?",
    icon: Cpu,
    fields: [
      {
        name: "ai_needs",
        label: "Would you like help with:",
        type: "checkbox",
        options: [
          { v: "ai_music_videos", l: "Music Videos" },
          { v: "reels", l: "Short-form Reels" },
          { v: "cover_art", l: "Cover Art" },
          { v: "content_calendar", l: "Content Calendar" },
          { v: "social_growth", l: "Social Media Growth" },
          { v: "ads", l: "Ads" },
          { v: "website", l: "Website" },
          { v: "funnel", l: "Sales Funnel" },
        ],
      },
    ],
  },
  {
    id: "goals",
    tag: "SECTION 15",
    title: "Goals",
    subtitle: "Speak it into existence.",
    icon: Target,
    fields: [
      {
        name: "goals_12mo",
        label: "What would make the next 12 months a success?",
        type: "textarea",
        placeholder: "Anything from '10k monthly listeners' to 'sync placement on a Netflix show'…",
      },
    ],
  },
  {
    id: "final",
    tag: "THE FINAL QUESTION",
    title: "The one that closes the deal",
    subtitle:
      "If we showed you money you're entitled to collect — or systems to strengthen your music business — would you be interested in having E-Quantum help you get everything set up correctly?",
    icon: Flame,
    fields: [
      {
        name: "interest_level",
        label: "Your honest answer.",
        type: "radio",
        options: [
          { v: "yes", l: "Yes" },
          { v: "maybe", l: "Maybe" },
          { v: "not_now", l: "Not Right Now" },
        ],
      },
    ],
  },
  {
    id: "wow",
    tag: "THE WOW MOMENT",
    title: "One more question.",
    subtitle:
      "If someone played one of your songs in a restaurant in Tokyo tomorrow, are you confident every royalty that belongs to you would eventually find its way into your bank account?",
    icon: Sparkles,
    fields: [
      {
        name: "confident_about_japan",
        label: "Answer from the gut.",
        type: "radio",
        options: [
          { v: "yes", l: "Yes — every penny." },
          { v: "no", l: "Honestly, no." },
        ],
      },
    ],
  },
];

const initialAnswers = {
  songs_released: 0,
  unreleased_music: "no",
  roles: [],
  distributor: "",
  pro: "None",
  publisher_account: "writer_only",
  royalties_collected: [],
  metadata_complete: [],
  soundexchange: "dont_know",
  songs_registered_pro: "no",
  split_agreements: "no",
  publishing_admin: "none",
  platforms: [],
  business_items: [],
  monthly_listeners: 0,
  instagram_followers: 0,
  tiktok_followers: 0,
  email_list_size: 0,
  biggest_fan_source: "",
  ai_needs: [],
  goals_12mo: "",
  interest_level: "maybe",
  confident_about_japan: "no",
};

function Field({ f, value, onChange }) {
  if (f.type === "checkbox") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-3">
        {f.options.map((opt) => {
          const checked = arr.includes(opt.v);
          return (
            <label
              key={opt.v}
              data-testid={`audit-option-${f.name}-${opt.v}`}
              className={
                "flex items-center gap-4 border p-4 cursor-pointer transition-colors " +
                (checked
                  ? "border-[var(--primary)] bg-[var(--primary)]/5"
                  : "border-white/10 hover:border-white/30")
              }
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => {
                  const next = e.target.checked ? [...arr, opt.v] : arr.filter((x) => x !== opt.v);
                  onChange(next);
                }}
                className="w-4 h-4 accent-[var(--primary)]"
              />
              <span className="text-sm text-zinc-100">{opt.l}</span>
            </label>
          );
        })}
      </div>
    );
  }
  if (f.type === "radio") {
    return (
      <div className="space-y-3">
        {f.options.map((opt) => {
          const checked = value === opt.v;
          return (
            <label
              key={opt.v || "none"}
              data-testid={`audit-option-${f.name}-${opt.v || "none"}`}
              className={
                "flex items-center gap-4 border p-4 cursor-pointer transition-colors " +
                (checked
                  ? "border-[var(--primary)] bg-[var(--primary)]/5"
                  : "border-white/10 hover:border-white/30")
              }
            >
              <input
                type="radio"
                name={f.name}
                checked={checked}
                onChange={() => onChange(opt.v)}
                className="w-4 h-4 accent-[var(--primary)]"
              />
              <span className="text-sm text-zinc-100">{opt.l}</span>
            </label>
          );
        })}
      </div>
    );
  }
  if (f.type === "textarea") {
    return (
      <textarea
        data-testid={`audit-input-${f.name}`}
        rows={5}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={f.placeholder}
        className="w-full bg-transparent border border-white/10 focus:border-[var(--primary)] outline-none px-4 py-3 text-white placeholder-zinc-600 transition-colors"
      />
    );
  }
  return (
    <input
      data-testid={`audit-input-${f.name}`}
      type={f.type}
      min={f.min}
      required={f.required}
      value={value ?? ""}
      onChange={(e) => onChange(f.type === "number" ? Number(e.target.value || 0) : e.target.value)}
      placeholder={f.placeholder}
      className="w-full bg-transparent border border-white/10 focus:border-[var(--primary)] outline-none px-4 py-3 text-white placeholder-zinc-600 transition-colors"
    />
  );
}

const TIER_META = {
  quantum_ready: { color: "#d4af37", glow: "rgba(212,175,55,0.5)", icon: "⚡" },
  strong_foundation: { color: "#22c55e", glow: "rgba(34,197,94,0.4)", icon: "◆" },
  revenue_leaks: { color: "#f59e0b", glow: "rgba(245,158,11,0.4)", icon: "⚠" },
  critical_missing: { color: "#ef4444", glow: "rgba(239,68,68,0.4)", icon: "▲" },
  emergency: { color: "#f43f5e", glow: "rgba(244,63,94,0.55)", icon: "!" },
};

function loadDraft() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

export default function ArtistAudit() {
  // Lazy initializers hydrate from localStorage BEFORE any effect runs,
  // avoiding the classic race where a persist-effect fires on mount with
  // stale initial-state closure and overwrites the restored draft.
  const [step, setStep] = useState(() => {
    const d = loadDraft();
    return typeof d?.step === "number" ? d.step : 0;
  });
  const [answers, setAnswers] = useState(() => {
    const d = loadDraft();
    return { ...initialAnswers, ...(d?.answers || {}) };
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const nav = useNavigate();

  // Persist on change (safe now — first run writes back the just-restored state)
  useEffect(() => {
    if (result) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ answers, step }));
    } catch (e) {
      // Non-fatal: user hit storage quota, private-mode, or storage disabled.
      // Log for observability without breaking the wizard flow.
      console.debug("audit draft persist skipped:", e?.name || e);
    }
  }, [answers, step, result]);

  const total = sections.length;
  const current = sections[step];
  const progress = ((step + 1) / total) * 100;

  const setField = (name) => (v) => setAnswers((a) => ({ ...a, [name]: v }));

  const stepIsValid = useMemo(() => {
    if (!current) return true;
    return current.fields.every((f) => {
      if (!f.required) return true;
      const v = answers[f.name];
      if (f.type === "email") return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || ""));
      return v !== undefined && v !== null && String(v).trim() !== "";
    });
  }, [current, answers]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/audit/submit", answers);
      setResult(data);
      localStorage.removeItem(STORAGE_KEY);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't submit your audit — please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  const next = async () => {
    if (!stepIsValid) {
      toast.message("A required field is missing.");
      return;
    }
    if (step === total - 1) {
      await submit();
      return;
    }
    setStep((s) => Math.min(total - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const back = () => {
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const restart = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAnswers(initialAnswers);
    setStep(0);
    setResult(null);
  };

  // ============ RESULT SCREEN ============
  if (result) {
    const meta = TIER_META[result.tier_id] || TIER_META.strong_foundation;
    return (
      <div className="min-h-screen bg-[#050505] text-white py-20 px-6">
        <div className="max-w-3xl mx-auto" data-testid="audit-result">
          <div className="font-mono text-[10px] tracking-[0.28em] text-[var(--primary)] uppercase mb-6">
            / your e-quantum score
          </div>

          <div
            className="border p-10 md:p-14 text-center relative"
            style={{ borderColor: meta.color, boxShadow: `0 0 60px -20px ${meta.glow}` }}
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Artist Infrastructure Audit&trade;</div>
            <div
              className="font-heading font-black text-8xl md:text-9xl leading-none tracking-tighter"
              style={{ color: meta.color }}
              data-testid="audit-score"
            >
              {result.score}
            </div>
            <div className="text-2xl md:text-3xl font-heading font-black tracking-tighter mt-4">
              {result.tier_label} <span className="ml-2" style={{ color: meta.color }}>{meta.icon}</span>
            </div>
            <div className="h-px w-14 mx-auto my-6" style={{ background: meta.color }} />
            <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-xl mx-auto">
              {result.tier_message}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mt-12">
            <div>
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-4">// what we found</div>
              <div className="border border-white/10">
                {Object.entries(result.breakdown).map(([k, v], i) => (
                  <div key={k} className={"flex items-center justify-between px-4 py-3 " + (i > 0 ? "border-t border-white/5" : "")}>
                    <span className="text-sm text-zinc-300">{k}</span>
                    <span className="font-mono text-sm text-white">{v} pts</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-4">// your action items</div>
              <ul className="space-y-3">
                {(result.action_items?.length ? result.action_items : ["You are set up better than most independent artists. Let's amplify what's already working."]).map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm text-zinc-200 leading-relaxed">
                    <CheckCircle2 size={16} className="text-[var(--primary)] mt-0.5 flex-shrink-0" strokeWidth={2} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 bg-[#0a0a0a] border border-white/10 p-8 md:p-10 text-center">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--primary)] mb-3">// next step</div>
            <h3 className="font-heading font-black text-3xl md:text-4xl tracking-tighter mb-3">
              Turn this score into a plan.
            </h3>
            <p className="text-zinc-400 text-sm md:text-base mb-8 max-w-lg mx-auto">
              A 30-minute consult where we walk through your audit line-by-line and map exactly what to fix first.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={() => nav(`/booking?email=${encodeURIComponent(answers.email)}&audit_score=${result.score}`)}
                className="btn-primary"
                data-testid="audit-book-consult"
              >
                Book my consult <ArrowUpRight size={14} />
              </button>
              <button onClick={restart} className="btn-ghost" data-testid="audit-restart">
                Take audit again
              </button>
            </div>
            <p className="text-xs text-zinc-500 font-mono mt-8">
              We just emailed a copy of your score + action items to {answers.email}.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============ WIZARD ============
  const Icon = current.icon;
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="audit-wizard">
      <Seo
        title="Free Artist Infrastructure Audit — 17-step royalty review"
        description="Take the free 17-step Artist Infrastructure Audit. We analyze publishing, PRO registration, distribution, metadata, and uncollected royalties, then return a score and specific recommendations."
        path="/audit"
      />
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-30 h-1 bg-white/5">
        <div
          className="h-full bg-[var(--primary)] transition-[width] duration-500"
          style={{ width: `${progress}%` }}
          data-testid="audit-progress"
        />
      </div>

      <div className="max-w-2xl mx-auto px-6 py-16 md:py-24">
        <div className="flex items-center justify-between mb-10">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[var(--primary)]">
            {current.tag}
          </div>
          <div className="font-mono text-[10px] text-zinc-600">
            {step + 1} / {total}
          </div>
        </div>

        <div className="flex items-start gap-4 mb-8">
          <div className="w-12 h-12 border border-[var(--primary)]/40 bg-[var(--primary)]/5 flex items-center justify-center text-[var(--primary)] flex-shrink-0 mt-1">
            <Icon size={20} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-heading font-black text-3xl md:text-5xl tracking-tighter leading-[0.95]">
              {current.title}
            </h1>
            <p className="text-zinc-400 text-sm md:text-base mt-3 leading-relaxed">
              {current.subtitle}
            </p>
          </div>
        </div>

        <div className="space-y-8 mt-12">
          {current.fields.map((f) => (
            <div key={f.name}>
              <label className="block text-sm font-medium text-zinc-100 mb-3">
                {f.label} {f.required && <span className="text-[var(--primary)]">*</span>}
              </label>
              {f.help && <p className="text-xs text-zinc-500 mb-3 leading-relaxed italic">{f.help}</p>}
              <Field f={f} value={answers[f.name]} onChange={setField(f.name)} />
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-14 pt-8 border-t border-white/5">
          <button
            onClick={back}
            disabled={step === 0}
            data-testid="audit-back-btn"
            className="btn-ghost disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={14} /> Back
          </button>
          <button
            onClick={next}
            disabled={submitting}
            data-testid="audit-next-btn"
            className="btn-primary"
          >
            {submitting ? (
              <><Loader2 size={14} className="animate-spin" /> Scoring…</>
            ) : step === total - 1 ? (
              <>Get my score <Sparkles size={14} /></>
            ) : (
              <>Next <ChevronRight size={14} /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
